import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { VendasList } from './comercial/VendasList';
import { LocacoesList } from './comercial/LocacoesList';
import { ProcessoForm } from './comercial/ProcessoForm';
import { ProcessoDetailModal } from './comercial/ProcessoDetailModal';
import { 
  Plus, 
  FileText, 
  TrendingUp, 
  CheckCircle,
  Clock
} from 'lucide-react';

export type ProcessoComercial = {
  id: string;
  tipo: 'venda' | 'locacao' | 'prestacao_servico';
  status: 'criado' | 'coletando_dados' | 'contrato_rascunho' | 'revisao' | 'aprovado' | 'finalizado' | 'cancelado';
  titulo: string;
  unidade?: string;
  property_id?: string;
  responsavel_id?: string;
  template_contrato_id?: string;
  link_formulario?: string;
  data_inicio?: string;
  data_conclusao?: string;
  observacoes?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  responsavel?: {
    first_name: string;
    last_name: string;
  };
  property?: {
    title: string;
    listing_id: string;
  };
  partes?: Array<{
    id: string;
    tipo_parte: string;
    nome_completo: string;
    status_documentos: string;
  }>;
  contratos?: Array<{
    id: string;
    status: string;
    versao: number;
  }>;
};

interface ComercialDashboardProps {
  initialTab?: 'vendas' | 'locacoes';
}

const ComercialDashboard: React.FC<ComercialDashboardProps> = ({ initialTab = 'vendas' }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const activeTab = initialTab;
  const [showForm, setShowForm] = useState(false);
  const [selectedProcesso, setSelectedProcesso] = useState<ProcessoComercial | null>(null);
  const [editingProcesso, setEditingProcesso] = useState<ProcessoComercial | null>(null);
  const [processes, setProcesses] = useState<ProcessoComercial[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    vendas: { total: 0, ativos: 0, finalizados: 0, pendentes: 0 },
    locacoes: { total: 0, ativos: 0, finalizados: 0, pendentes: 0 }
  });

  useEffect(() => {
    loadProcesses();
  }, [user]);

  const loadProcesses = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('processos_comerciais')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processData = (data || []) as unknown as ProcessoComercial[];
      setProcesses(processData);

      // Calculate stats
      const vendas = processData.filter(p => p.tipo === 'venda');
      const locacoes = processData.filter(p => p.tipo === 'locacao');

      setStats({
        vendas: {
          total: vendas.length,
          ativos: vendas.filter(p => ['criado', 'coletando_dados', 'contrato_rascunho', 'revisao'].includes(p.status)).length,
          finalizados: vendas.filter(p => p.status === 'finalizado').length,
          pendentes: vendas.filter(p => ['coletando_dados', 'revisao'].includes(p.status)).length
        },
        locacoes: {
          total: locacoes.length,
          ativos: locacoes.filter(p => ['criado', 'coletando_dados', 'contrato_rascunho', 'revisao'].includes(p.status)).length,
          finalizados: locacoes.filter(p => p.status === 'finalizado').length,
          pendentes: locacoes.filter(p => ['coletando_dados', 'revisao'].includes(p.status)).length
        }
      });
    } catch (error) {
      console.error('Erro ao carregar processos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os processos comerciais",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProcess = () => {
    setEditingProcesso(null);
    setShowForm(true);
  };

  const handleEditProcess = (processo: ProcessoComercial) => {
    setEditingProcesso(processo);
    setShowForm(true);
  };

  const handleViewProcess = (processo: ProcessoComercial) => {
    setSelectedProcesso(processo);
  };

  const handleDeleteProcess = async (processo: ProcessoComercial) => {
    if (!window.confirm(`Tem certeza que deseja excluir o processo "${processo.titulo}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('processos_comerciais')
        .delete()
        .eq('id', processo.id);

      if (error) throw error;

      toast({
        title: "Processo excluído",
        description: "O processo foi excluído com sucesso.",
      });

      loadProcesses();
    } catch (error) {
      console.error('Erro ao excluir processo:', error);
      toast({
        title: "Erro ao excluir",
        description: "Não foi possível excluir o processo. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingProcesso(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProcesso(null);
    loadProcesses();
    toast({
      title: "Sucesso",
      description: editingProcesso ? "Processo atualizado com sucesso" : "Processo criado com sucesso",
    });
  };

  const handleCloseDetail = () => {
    setSelectedProcesso(null);
    loadProcesses(); // Reload to get any updates
  };

  const currentTypeProcesses = processes.filter(p => p.tipo === (activeTab === 'vendas' ? 'venda' : 'locacao'));
  const currentStats = stats[activeTab];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Módulo Comercial</h1>
          <p className="text-muted-foreground">
            Gerencie processos de vendas e locações de forma organizada
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total de Processos</p>
                <p className="text-2xl font-bold text-foreground">{currentStats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </div>

          <div className="bg-card rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Processos Ativos</p>
                <p className="text-2xl font-bold text-foreground">{currentStats.ativos}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-card rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Finalizados</p>
                <p className="text-2xl font-bold text-foreground">{currentStats.finalizados}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-card rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold text-foreground">{currentStats.pendentes}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-card rounded-lg border">
          <div className="border-b border-border p-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                {activeTab === 'vendas' ? 'Processos de Venda' : 'Processos de Locação'}
              </h2>
            </div>
            
            <Button onClick={handleCreateProcess} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Novo Processo
            </Button>
          </div>

          <div className="p-6">
            {activeTab === 'vendas' ? (
              <VendasList
                processes={currentTypeProcesses}
                onEdit={handleEditProcess}
                onView={handleViewProcess}
                onDelete={handleDeleteProcess}
                onRefresh={loadProcesses}
              />
            ) : (
              <LocacoesList
                processes={currentTypeProcesses}
                onEdit={handleEditProcess}
                onView={handleViewProcess}
                onDelete={handleDeleteProcess}
                onRefresh={loadProcesses}
              />
            )}
          </div>
        </div>
      </div>

      {/* Process Form Modal */}
      {showForm && (
        <ProcessoForm
          isOpen={showForm}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
          processo={editingProcesso}
          tipo={activeTab === 'vendas' ? 'venda' : 'locacao'}
        />
      )}

      {/* Process Detail Modal */}
      {selectedProcesso && (
        <ProcessoDetailModal
          isOpen={!!selectedProcesso}
          onClose={handleCloseDetail}
          processo={selectedProcesso}
        />
      )}
    </div>
  );
};

export default ComercialDashboard;